import type { AlertItem, AlertSeverity } from '../pages/markets/api/types';
import { toIso } from './time';

/**
 * Firestore: users/{uid}/subscriptions/{event_id}
 * Created when the user clicks the bell on an event page.
 */
export interface SubscriptionDoc {
    event_id: string;
    /** Human-readable event name, stored at subscribe time for display in the
     *  subscriptions table. Optional: subscriptions created before this field
     *  existed fall back to event_id. */
    event_title?: string;
    /** Server timestamp. Compared against alert.created_at in fan-out. */
    created_at: unknown;
    /** null = all types enabled. */
    alert_types: string[] | null;
    min_severity: AlertSeverity;
}

/**
 * Firestore: users/{uid}/notifications/{notif_id}
 * notif_id = sha1(uid + alert.id) — idempotent across retries.
 */
export interface NotificationDoc {
    notif_id: string;
    /** Verbatim copy of the AlertItem from the indexer. */
    alert: AlertItem;
    /** Denormalized for the bell dropdown so we don't lookup per render. */
    event_title: string;
    /** Server timestamp set by fan-out. */
    delivered_at: unknown;
    read: boolean;
    /** alert.ts + 30d, Firestore TTL field. */
    expires_at: unknown;
}

/**
 * Coerce `alert.ts` to the ISO string its type claims — gate_request alerts
 * carry the gate doc's `updated_at`, which bogg ships as a native Timestamp
 * (`new Date(Timestamp)` is Invalid). Mirrors normalizeGateDoc in ./gates.ts.
 * Needed on top of the producer fix: delivered docs keep the bad shape for 30d.
 */
export function normalizeNotificationDoc(
    raw: Record<string, unknown>
): NotificationDoc {
    const alert = raw.alert as Record<string, unknown> | undefined;
    if (!alert || typeof alert.ts === 'string')
        return raw as unknown as NotificationDoc;
    // '' over the raw value: rows guard NaN and render an em-dash, whereas a
    // leaked Timestamp object renders "Invalid Date". An absent ts is
    // materialized as '' too — same rendering, and not worth a second branch.
    return {
        ...raw,
        alert: { ...alert, ts: toIso(alert.ts) ?? '' },
    } as unknown as NotificationDoc;
}

/**
 * Firestore: users/{uid}/notification_settings (single doc)
 */
export interface NotificationSettingsDoc {
    min_severity: AlertSeverity;
    /** null = all types enabled globally. */
    enabled_types: string[] | null;
    fcm_tokens: Record<string, { created_at: unknown; user_agent: string }>;
    /**
     * #118: gate accounts this user muted — gate_request alerts for these
     * are suppressed entirely in the fan-out (no bell doc, no push).
     * Missing/absent = nothing muted (gate alerts otherwise bypass all
     * filters by design).
     */
    muted_gate_accounts?: string[];
}

/**
 * Firestore: subscribed_events/{event_id}
 * Denormalized count of subscribers per event. Doc deleted when count
 * reaches 0. Maintained by maintainSubscribedEvents trigger.
 */
export interface SubscribedEventsDoc {
    event_id: string;
    subscriber_count: number;
    updated_at: unknown;
}

/**
 * Firestore: users/{uid}/trader_subscriptions/{address}
 * Created when the user clicks the bell on a trader. Mirror of SubscriptionDoc
 * keyed by on-chain address.
 */
export interface TraderSubscriptionDoc {
    trader_address: string;
    /** Human-readable label captured at subscribe time; falls back to address. */
    trader_label?: string;
    /** Server timestamp. Compared against alert.created_at in fan-out. */
    created_at: unknown;
    min_severity: AlertSeverity;
    /** Mute toggle. false = keep the sub but stop delivery (silence a chatty trader). */
    enabled: boolean;
}

/**
 * Firestore: subscribed_traders/{address}
 * Denormalized subscriber count per trader. Doc deleted at 0. Maintained by
 * the maintainSubscribedTraders trigger; read by bogg + the indexer.
 */
export interface SubscribedTradersDoc {
    trader_address: string;
    subscriber_count: number;
    updated_at: unknown;
}

/**
 * Firestore: alerts_outbox/{batch_id}
 * Handoff from indexer to fan-out function. 24h TTL safety net.
 */
export interface AlertsOutboxDoc {
    batch_id: string;
    created_at: unknown;
    expires_at: unknown;
    alerts: Array<AlertItem & { event_title: string }>;
}

/**
 * Firestore: indexer_state/notification_pusher
 * Watermark for at-least-once delivery on indexer restart.
 */
export interface NotificationPusherStateDoc {
    /** = max(alert.created_at) flushed so far (Firestore timestamp). */
    watermark_ts: unknown;
    /** = alert.id from that row (UUID string), tie-breaker. */
    watermark_id: string;
    updated_at: unknown;
    /** Pod id / hostname of the holder that wrote this. */
    holder: string;
}

/**
 * Firestore: indexer_leases/notification_pusher
 * Single-leader election. 30s TTL, 10s renewal.
 */
export interface NotificationPusherLeaseDoc {
    holder: string;
    acquired_at: unknown;
    expires_at: unknown;
}
